/**
 * ProjectEditor Component
 *
 * Tabbed editor for a single DC project.
 * Sections are defined in appConfig.js.
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import * as Icons from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { useAppProject } from '../hooks/useAppProject';
import { appConfig } from '../config/appConfig';

// Import sections
import SiteSection from '../components/sections/SiteSection';
import ITLoadSection from '../components/sections/ITLoadSection';
import InfrastructureSection from '../components/sections/InfrastructureSection';
import InterconnectionSection from '../components/sections/InterconnectionSection';
import TariffSection from '../components/sections/TariffSection';
import FinancialSection from '../components/sections/FinancialSection';
import ResultsDashboard from '../components/sections/ResultsDashboard';

// Map section keys to components
const SECTION_COMPONENTS = {
  site: SiteSection,
  it_load: ITLoadSection,
  infrastructure: InfrastructureSection,
  interconnection: InterconnectionSection,
  tariff: TariffSection,
  financial: FinancialSection,
  results: ResultsDashboard,
};

// All navigable tabs: appConfig sections + results
const ALL_SECTIONS = [
  ...appConfig.sections,
  { key: 'results', label: 'Results', icon: 'ShowChart' },
];

function ProjectEditor() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { project, loading, error, updateProject } = useAppProject(projectId);

  const [activeSection, setActiveSection] = useState(ALL_SECTIONS[0]?.key || 'site');

  const handleBack = () => {
    navigate(appConfig.appPath);
  };

  const handleUpdateProject = async (updates) => {
    try {
      await updateProject(updates);
    } catch (err) {
      console.error('Failed to update DC project:', err);
    }
  };

  if (loading && !project) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: vortxColors.gold }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning">Project not found</Alert>
      </Container>
    );
  }

  const ActiveSectionComponent = SECTION_COMPONENTS[activeSection];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          onClick={handleBack}
          sx={{
            color: vortxColors.gold,
            mr: 2,
            '&:hover': {
              bgcolor: 'rgba(178, 140, 80, 0.1)',
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontFamily: vortxTypography.heading,
            fontWeight: 700,
            color: vortxColors.white,
          }}
        >
          {project.name}
        </Typography>
      </Box>

      {/* Section Navigation */}
      <Box sx={{ borderBottom: `2px solid ${vortxColors.gold}`, mb: 2 }}>
        <Tabs
          value={activeSection}
          onChange={(e, newValue) => setActiveSection(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: vortxColors.gold,
              height: 3,
            },
            '& .MuiTab-root': {
              color: vortxColors.stone,
              fontWeight: 500,
              minHeight: 48,
              '&.Mui-selected': {
                color: vortxColors.gold,
              },
              '&:hover': {
                color: vortxColors.white,
                bgcolor: 'rgba(178, 140, 80, 0.1)',
              },
            },
          }}
        >
          {ALL_SECTIONS.map((section) => {
            const IconComponent = Icons[section.icon] || Icons.Help;
            return (
              <Tab
                key={section.key}
                value={section.key}
                label={section.label}
                icon={<IconComponent />}
                iconPosition="start"
              />
            );
          })}
        </Tabs>
      </Box>

      {/* Active Section */}
      <Paper
        sx={{
          bgcolor: vortxColors.charcoal,
          border: `1px solid ${vortxColors.teal}`,
          p: 3,
          mt: 3,
        }}
      >
        {ActiveSectionComponent ? (
          <ActiveSectionComponent project={project} onUpdate={handleUpdateProject} />
        ) : (
          <Alert severity="warning">Section component not found: {activeSection}</Alert>
        )}
      </Paper>
    </Container>
  );
}

export default ProjectEditor;
